import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Activity } from "lucide-react";
import api from "@/lib/api";
import { EmptyState, ErrorState, Skel } from "@/components/shally/ui";

const lbl = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" });

function Tip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-md">
      <p className="mb-1 font-semibold text-foreground">{lbl(label)}</p>
      <p className="text-muted-foreground">{p.total} call{p.total === 1 ? "" : "s"} · {p.answered} answered · {p.missed} missed</p>
    </div>);
}

export function ActivityChart({ range }) {
  const { data, error, refetch, isLoading } = useQuery({ queryKey: ["ci-activity", range], queryFn: () => api.get("/call-intel/activity", { params: { range } }).then((r) => r.data) });
  if (error) return <ErrorState error={error} onRetry={refetch} testid="ci-activity-error" />;
  if (isLoading) return <Skel className="h-56 w-full" />;
  const days = data.days;
  if (!days.some((d) => d.total)) return <EmptyState icon={Activity} title="No call activity in this period" text="Calls Shally handles will appear here day by day." testid="ci-activity-empty" />;
  return (
    <div className="h-56 w-full" data-testid="ci-activity-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={days} margin={{ top: 4, right: 4, left: -24, bottom: 0 }} barCategoryGap="28%">
          <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
          <XAxis dataKey="date" tickFormatter={lbl} tickLine={false} axisLine={false} minTickGap={24} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
          <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
          <Tooltip content={<Tip />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.6 }} />
          <Bar dataKey="answered" stackId="a" fill="hsl(var(--primary))" />
          <Bar dataKey="missed" stackId="a" fill="hsl(var(--destructive))" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>);
}

export function ChartLegend() {
  return (
    <span className="hidden items-center gap-3 text-[11px] text-muted-foreground sm:inline-flex">
      <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-primary" />Answered</span>
      <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-[hsl(var(--destructive))]" />Missed</span>
    </span>);
}
